import { ConditionalTooltipWrapper } from 'components/Tooltip';
import React from 'react';

import styles from './styles.module.css';

// Segment type definition
export type Segment = {
  /** Value for the segment (used to calculate percentage) */
  value: number;
  /** Bootstrap color for the segment (e.g., 'danger', 'success', etc.) */
  color:
    | 'primary'
    | 'secondary'
    | 'danger'
    | 'success'
    | 'warning'
    | 'info'
    | 'light'
    | 'dark'
    | 'white'
    | 'muted'
    | 'urgent'
    | 'critical'
    | 'serious'
    | 'medium'
    | 'minimal';
  /** Optional label for the segment (e.g., 'Failed', 'Passed') */
  label?: string;
};

// Props for the SegmentedBar component
export type BarSegmentsProps = {
  /** Array of segments to display in the progress bar */
  segments: Segment[];
  /** Optional height for the progress bar */
  height?: string;
  /** Optional width for the progress bar */
  width?: string;
  /** Optional className for additional styling */
  className?: string;
  /** Optional custom tooltip content (overrides default tooltip) */
  tooltip?: string | JSX.Element;
  /**
   * Label display mode: 'none' (no labels), 'value' (show values), or 'percentage' (show percentages)
   */
  labelDisplay?: 'none' | 'value' | 'percentage';
};

/**
 * SegmentedBar component displays a horizontal stacked bar divided into multiple segments.
 * It supports customizable labels and is optimized for use in table cells.
 */
const SegmentedBar: React.FC<BarSegmentsProps> = ({
  segments,
  height = '20px',
  width = '100%',
  className = '',
  tooltip,
  labelDisplay = 'percentage',
}) => {
  // Calculate the total value of all segments
  const totalValue = segments.reduce((sum, segment) => sum + segment.value, 0);

  /**
   * Renders segment labels based on the `labelDisplay` mode.
   * - 'none': No labels are displayed.
   * - 'value': Displays the raw value of each segment.
   * - 'percentage': Displays the percentage of each segment.
   */
  const renderSegmentLabels = (segments: Segment[]) => {
    if (labelDisplay === 'none' || totalValue === 0) return null;

    return (
      <div className="d-flex justify-content-between" style={{ width }}>
        {segments.map((segment, index) => {
          const percentage =
            totalValue > 0 ? (segment.value / totalValue) * 100 : 0;
          const labelContent =
            labelDisplay === 'value'
              ? segment.value
              : `${percentage.toFixed(1)}%`;

          return (
            <small key={index} className={`text-${segment.color}`}>
              {segment.label}: {labelContent}
            </small>
          );
        })}
      </div>
    );
  };

  /**
   * Renders the segmented bar with individual segments.
   * Each segment is styled based on its color and width (calculated as a percentage).
   */
  return (
    <ConditionalTooltipWrapper tooltip={tooltip}>
      <div
        className={`${styles.segmentedBar} ${className}`}
        style={{ height, width }}
        role="group"
        aria-label="Segmented bar chart"
      >
        {segments.map((segment, index) => {
          const percentage =
            totalValue > 0 ? (segment.value / totalValue) * 100 : 0;
          return (
            <div
              key={index}
              className={`${styles.segment} bg-${segment.color}`}
              style={{
                width: `${percentage > 0 && percentage < 1 ? 1 : percentage}%`,
              }}
              role="none"
              title={`${segment.label} (${percentage.toFixed(1)}%)`}
              aria-label={`${segment.label}: ${percentage.toFixed(1)}%`}
            ></div>
          );
        })}
      </div>
      {renderSegmentLabels(segments)}
    </ConditionalTooltipWrapper>
  );
};

export default SegmentedBar;
